import React, { useState, useEffect } from "react";
import { apiGet, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { isEmpty, startCase } from "lodash";
import helpers from "utils/helpers";
import { AiOutlineSwap } from "react-icons/ai";
import useToastContext from "hooks/useToastContext";

const SwapRequestsModal = ({ show, handleClose, refreshShifts }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [swapRequests, setSwapRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getSwapRequests = async () => {
    try {
      setIsLoading(true);
      const res = await apiGet(apiPath.getSwapRequests, {});
      const response = res?.data?.results;
      
      if (Array.isArray(response)) {
        setSwapRequests(response);
      } else if (response?.docs) {
        setSwapRequests(response.docs);
      } else {
        setSwapRequests([]);
      }
    } catch (error) {
      console.log("Error fetching swap requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      getSwapRequests();
    }
  }, [show]);

  const handleApproveSwap = async (shiftId) => {
    try {
      const res = await apiPut(`${apiPath.shiftSwapApprove}/${shiftId}/swap-approve`, {});
      if (res?.data?.success) {
        notification.success(t("SWAP_APPROVED_SUCCESS"));
        getSwapRequests();
        if (refreshShifts) refreshShifts();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (error) {
      notification.error(t("SOMETHING_WENT_WRONG"));
    }
  };

  const handleRejectSwap = async (shiftId) => {
    try {
      const res = await apiPut(`${apiPath.shiftSwapReject}/${shiftId}/swap-reject`, {});
      if (res?.data?.success) {
        notification.success(t("SWAP_REJECTED_SUCCESS"));
        getSwapRequests();
        if (refreshShifts) refreshShifts();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (error) {
      notification.error(t("SOMETHING_WENT_WRONG"));
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black opacity-40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-4xl pointer-events-auto">
          <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none max-h-[85vh]">
            
            {/* Header */}
            <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
              <div className="flex items-center gap-2">
                <AiOutlineSwap className="w-5 h-5 text-orange-500" />
                <h3 className="font-bold text-lg text-gray-800">
                  {t("SWAP_REQUESTS")}
                </h3>
                {swapRequests.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {swapRequests.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl font-light">×</span>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto max-h-[65vh]">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="loading-spinner h-8 w-8 border-2 border-[#4F46E5]/30 border-t-[#4F46E5] rounded-full animate-spin"></div>
                </div>
              ) : swapRequests.length === 0 ? (
                <div className="text-center py-12">
                  <AiOutlineSwap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-gray-500 font-medium">{t("NO_SWAP_REQUESTS")}</h4>
                  <p className="text-gray-400 text-sm mt-1">{t("NO_SWAP_REQUESTS_DESC")}</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs text-left text-[#A5A5A5]">
                    <thead className="text-gray-900 text-xs border-b bg-[#E1E6EE]">
                      <tr>
                        <th className="px-4 py-3">{t("SHIFT_NAME")}</th>
                        <th className="px-4 py-3">{t("DATE")}</th>
                        <th className="px-4 py-3">{t("TIME")}</th>
                        <th className="px-4 py-3">{t("CURRENT_EMPLOYEE")}</th>
                        <th className="px-4 py-3">{t("REQUESTED_EMPLOYEE")}</th>
                        <th className="px-4 py-3">{t("REASON")}</th>
                        <th className="px-4 py-3 text-center">{t("O_ACTION")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {swapRequests.map((item) => (
                        <tr key={item._id} className="bg-white border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900 font-medium">
                            {item?.name}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {item?.date ? dayjs(item.date).format("MM-DD-YYYY") : "N/A"}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {item?.startTime} - {item?.endTime}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-gray-900 font-medium">
                                {item?.employeeId?.name || "N/A"}
                              </span>
                              <br />
                              <span className="text-gray-400 text-xs">
                                {item?.employeeId?.employeeId || ""}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-orange-600 font-medium">
                                {item?.swapRequestedTo?.name || "N/A"}
                              </span>
                              <br />
                              <span className="text-gray-400 text-xs">
                                {item?.swapRequestedTo?.employeeId || ""}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">
                            {item?.swapReason || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() =>
                                  helpers.alertFunction(
                                    `Approve swap from ${item?.employeeId?.name} to ${item?.swapRequestedTo?.name}?`,
                                    item._id,
                                    handleApproveSwap,
                                    true
                                  )
                                }
                                className="px-3 py-1.5 text-xs font-medium bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                              >
                                {t("APPROVE")}
                              </button>
                              <button
                                onClick={() =>
                                  helpers.alertFunction(
                                    `Reject swap request for ${item?.name}?`,
                                    item._id,
                                    handleRejectSwap,
                                    true
                                  )
                                }
                                className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                              >
                                {t("REJECT")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SwapRequestsModal;